/**
* BehaviorValidators.ts
* @copyright Microsoft 2018
*/
import { getInst, isStrictNullOrUndefined, isStrictUndefined, isString, mathMax, strSplit } from "@nevware21/ts-utils";
import { Behavior } from "./Behaviors";
/**
 * Current version of the behaviors package, used for comparing with any globally loaded version.
 * This will be replaced during the build process with the actual version number.
 * The version string should follow semver format (e.g. '4.4.0') to ensure proper comparison.
 */
export var BehaviorsVersion = '4.4.0';
// Global namespace key for the 1DS SDK behaviors package when loaded via CDN/script tag
var _gblNsKey = "oneDS";
/**
 * Internal variable to hold the current version of the behaviors package, used for comparing with any globally loaded version.
 */
var _currentVersion;
/**
 * Internal variable to hold a reference to the globally loaded behaviors namespace if it has a newer version than this bundled package.
 * This allows the plugin to use the newer behavior validators without requiring explicit configuration.
 */
var _nsOverride;
/**
 * Per-function recursion guard flags to prevent stack overflow when the global namespace
 * points back to this module. Separate flags ensure that if a future global version's
 * getDefinedBehaviorOnly internally calls getAnyNumericBehavior (or vice versa),
 * only the originating function is blocked from re-delegating.
 */
var _delegatingDefined = false;
var _delegatingAny = false;
/**
 * Compare two semver-style version strings (e.g. '4.4.0' vs "4.5.1").
 * @returns A positive number if a > b, negative if a < b, or 0 if equal.
 */
function _compareVersions(a, b) {
    var partsA = strSplit((a || "0"), ".");
    var partsB = strSplit((b || "0"), ".");
    var len = mathMax(partsA.length, partsB.length);
    for (var i = 0; i < len; i++) {
        var numA = parseInt(partsA[i], 10) || 0;
        var numB = parseInt(partsB[i], 10) || 0;
        if (numA !== numB) {
            return numA - numB;
        }
    }
    return 0;
}
/**
 * Returns the globally loaded behaviors namespace if it has a newer version than
 * this bundled package, or null if no newer global version is available.
 */
function _getNewerGlobal() {
    var nsInst = getInst(_gblNsKey);
    if (nsInst) {
        var nsVersion = nsInst.BehaviorsVersion;
        if (nsVersion && _currentVersion !== nsVersion && _compareVersions(nsVersion, BehaviorsVersion) > 0) {
            _currentVersion = nsVersion;
            _nsOverride = nsInst;
        }
    }
    return _nsOverride;
}
/**
 * Parses a behavior value from a string or number input.
 * If the value is a number or numeric string, returns the parsed integer.
 * If the value is a string matching a Behavior enum name, returns the corresponding enum value.
 * Otherwise returns undefined.
 * @param behavior - The behavior value to parse
 * @returns The parsed numeric behavior value, or undefined if it cannot be parsed
 */
function _parseBehaviorValue(behavior) {
    var result = -1;
    if (!isStrictNullOrUndefined(behavior) && behavior !== "") {
        var value = parseInt(behavior);
        if (!isNaN(value)) {
            result = value;
        }
        else if (isString(behavior)) {
            var enumValue = Behavior[behavior];
            if (!isStrictUndefined(enumValue)) {
                result = enumValue;
            }
        }
    }
    return result;
}
/**
 * Strict behavior validator that checks if the value is a valid member of the Behavior enum.
 * Accepts both numeric values and string enum names.
 * Returns 0 (UNDEFINED) if the value is not a valid Behavior enum member.
 * @param behavior - The behavior value to validate
 * @returns The validated behavior number, or 0 (UNDEFINED) if invalid
 *
 * @example
 * ```typescript
 * import { getDefinedBehaviorOnly } from "@microsoft/1ds-behaviors-js";
 *
 * getDefinedBehaviorOnly(61);          // 61 (SEARCH is a defined behavior)
 * getDefinedBehaviorOnly("SEARCH");    // 61 (resolved from enum name)
 * getDefinedBehaviorOnly(999);         // 0 (not a defined Behavior member)
 * getDefinedBehaviorOnly(null);        // 0
 * ```
 */
export function getDefinedBehaviorOnly(behavior) {
    if (!_delegatingDefined) {
        var gbl = _getNewerGlobal();
        if (gbl && gbl.getDefinedBehaviorOnly && gbl.getDefinedBehaviorOnly !== getDefinedBehaviorOnly) {
            _delegatingDefined = true;
            try {
                return gbl.getDefinedBehaviorOnly(behavior);
            }
            catch (e) {
                // Fall back to local implementation if the global override throws
            }
            finally {
                _delegatingDefined = false;
            }
        }
    }
    var value;
    var result = _parseBehaviorValue(behavior);
    if (result >= 0 && result in Behavior) {
        value = result;
    }
    return value || 0; /*UNDEFINED*/
}
/**
 * Less strict behavior validator that accepts any positive integer as a valid behavior.
 * Does not check if the value is a member of the Behavior enum.
 * Only accepts values that parse to an integer > 0 and < 2147483647 (MaxInt).
 * Returns 0 (UNDEFINED) if the value cannot be parsed to a valid positive number.
 * @param behavior - The behavior value to validate
 * @returns The validated behavior number, or 0 (UNDEFINED) if not a valid positive number
 *
 * @example
 * ```typescript
 * import { getAnyNumericBehavior } from "@microsoft/1ds-behaviors-js";
 *
 * getAnyNumericBehavior(61);           // 61
 * getAnyNumericBehavior(999);          // 999 (accepted even though not in Behavior enum)
 * getAnyNumericBehavior("42");         // 42 (parsed from string)
 * getAnyNumericBehavior(-1);           // 0 (negative numbers rejected)
 * getAnyNumericBehavior(0);            // 0 (zero rejected)
 * getAnyNumericBehavior(null);         // 0
 * getAnyNumericBehavior("SEARCH");     // 0 (string enum names not supported)
 * ```
 */
export function getAnyNumericBehavior(behavior) {
    if (!_delegatingAny) {
        var gbl = _getNewerGlobal();
        if (gbl && gbl.getAnyNumericBehavior && gbl.getAnyNumericBehavior !== getAnyNumericBehavior) {
            _delegatingAny = true;
            try {
                return gbl.getAnyNumericBehavior(behavior);
            }
            catch (e) {
                // Fall back to local implementation if the global override throws
            }
            finally {
                _delegatingAny = false;
            }
        }
    }
    var value;
    var result = _parseBehaviorValue(behavior);
    if (result >= 0 && result <= 2147483647) {
        value = result;
    }
    return value || 0; /*UNDEFINED*/
}
/**
 * @internal
 * Test hook to reset the cached global namespace override.
 * This allows tests to start with a clean state.
 */
export function _resetGlobalCache() {
    _currentVersion = undefined;
    _nsOverride = undefined;
    _delegatingDefined = false;
    _delegatingAny = false;
}
//# sourceMappingURL=BehaviorValidators.js.map